import { getPostsApi } from '@/api-calls/posts/get'
import { useAuth } from '@/auth/auth-context'
import { ContainerWithSidebar } from '@/components/header/container'
import { useSite } from '@/hooks/use-site'
import { Button, Skeleton, ToggleGroup, ToggleGroupItem } from '@blogging/ui'
import { useFetch } from '@ngtrvu/use-api'
import { formatDistance } from 'date-fns'
import { ArrowRight, Lock, Pencil } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

type StatusFilter = 'all' | 'published' | 'draft'

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Published', value: 'published' },
  { label: 'Drafts', value: 'draft' },
]

function parseStatusFilter(value: string | null): StatusFilter {
  return STATUS_FILTERS.some((f) => f.value === value)
    ? (value as StatusFilter)
    : 'all'
}

function getStatusColor(status: string) {
  switch (status) {
    case 'draft':
      return 'text-gray-500'
    case 'published':
      return 'text-green-500'
    default:
      return 'text-gray-500'
  }
}

export function SitePostsPage() {
  const { siteId } = useParams()
  const { isLoading: isSiteLoading, canManageAllContent } = useSite()
  const { user } = useAuth()

  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = parseStatusFilter(searchParams.get('status'))

  const setStatusFilter = (value: StatusFilter) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === 'all') {
          next.delete('status')
        } else {
          next.set('status', value)
        }
        return next
      },
      { replace: true },
    )
  }

  const { data: posts, isLoading } = useFetch(getPostsApi, {
    resourceName: 'posts',
    initialParams: {
      siteId: siteId as string,
    },
  })

  const isLoadingData = isLoading || isSiteLoading

  const filteredPosts = useMemo(() => {
    if (!posts) return []
    if (statusFilter === 'all') return posts
    return posts.filter((post: any) => post.status === statusFilter)
  }, [posts, statusFilter])

  // Check if user can edit a specific post
  const canEditPost = (post: any) => {
    if (canManageAllContent) return true // Admin or Editor can edit all
    return post.createdById === user?.id // Author can only edit their own
  }

  return (
    <ContainerWithSidebar>
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Posts</h1>
        <div className="flex items-center gap-2">
          {!isLoadingData && posts && posts.length > 0 && (
            <ToggleGroup
              type="single"
              variant="outline"
              value={statusFilter}
              onValueChange={(value) =>
                value && setStatusFilter(value as StatusFilter)
              }
            >
              {STATUS_FILTERS.map((filter) => (
                <ToggleGroupItem key={filter.value} value={filter.value}>
                  {filter.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
          <Link to={`/${siteId}/editor/post/new`}>
            <Button variant="default">New post</Button>
          </Link>
        </div>
      </header>

      <main className="mt-6">
        {isLoadingData ? (
          <div className="space-y-3">
            <Skeleton className="h-[72px] w-full rounded-lg" />
          </div>
        ) : !posts || posts.length === 0 ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 my-8">
              Your blog is empty, create your first post to get started.
            </p>
            <Link to={`/${siteId}/editor/post/new`}>
              <Button variant="default">
                Create your first post <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        ) : filteredPosts.length === 0 ? (
          <p className="text-sm text-gray-500 my-8">
            No {statusFilter === 'all' ? '' : statusFilter} posts found.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredPosts.map((post: any) => {
              const { createdBy: author } = post
              const canEdit = canEditPost(post)

              return canEdit ? (
                <Link
                  key={post.id}
                  to={`/${siteId}/editor/post/${post.id}`}
                  className="flex items-center justify-between rounded-lg border p-4 hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <div className="space-y-1">
                    <h3 className="font-medium">{post.title}</h3>
                    <p className="text-sm text-gray-500">
                      By {author?.name} -{' '}
                      {formatDistance(post.createdAt, new Date(), {
                        addSuffix: true,
                      })}
                    </p>
                    <span className={`text-sm font-medium ${getStatusColor(post.status)}`}>
                      {post.status}
                    </span>
                  </div>
                  <div className="h-9 w-9 flex items-center justify-center">
                    <Pencil className="h-4 w-4" />
                  </div>
                </Link>
              ) : (
                <div
                  key={post.id}
                  className="flex items-center justify-between rounded-lg border p-4 bg-muted/30 cursor-not-allowed opacity-60"
                >
                  <div className="space-y-1">
                    <h3 className="font-medium">{post.title}</h3>
                    <p className="text-sm text-gray-500">
                      By {author?.name} -{' '}
                      {formatDistance(post.createdAt, new Date(), {
                        addSuffix: true,
                      })}
                    </p>
                    <span className={`text-sm font-medium ${getStatusColor(post.status)}`}>
                      {post.status}
                    </span>
                  </div>
                  <div className="h-9 w-9 flex items-center justify-center">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </ContainerWithSidebar>
  )
}
